<template>
  <!-- La posición inicial va en CSS y no en style inline: Vue re-aplica el
       atributo style estático en cada re-render (p. ej. al cambiar de capa),
       pisando el top/left que el usuario fijó al arrastrar el panel. -->
  <div
    class="chart-wrapper map-tool-window"
    :class="{ 'is-collapsed': isHidden }"
    id="geoprocessingChartID"
  >
    <div class="results-header map-tool-window-header" id="geoprocessingChartIDheader">
      <h5 class="map-tool-window-header-title">{{ resolvedTitle }}</h5>
      <div class="results-header-actions map-tool-window-header-actions">
        <button
          type="button"
          class="control-btn results-control-btn"
          @click="toggleVisible"
          :title="isHidden ? 'Expandir panel' : 'Minimizar panel'"
          :aria-label="isHidden ? 'Expandir panel' : 'Minimizar panel'"
          :aria-expanded="!isHidden"
        >
          <font-awesome-icon :icon="isHidden ? 'chevron-down' : 'chevron-up'" />
        </button>
        <button
          type="button"
          class="control-btn results-control-btn"
          @click="closeChart"
          title="Cerrar"
          aria-label="Cerrar resultados"
        >
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>
    <div v-show="!isHidden" class="chart-scroll-container">
      <div class="layer-selector-container">
        <!-- Pestañas de la herramienta -->
        <div class="tabs-container">
          <button
            v-if="hasPopulationData"
            type="button"
            class="btn btn-tab btn-tab--text"
            :class="{ 'active': chartAxis === 'poblacion' }"
            :aria-pressed="chartAxis === 'poblacion'"
            @click="chartAxis = 'poblacion'"
          >
            Población
          </button>
          <button
            type="button"
            class="btn btn-tab btn-tab--text"
            :class="{ 'active': chartAxis === 'infraestructura' }"
            :aria-pressed="chartAxis === 'infraestructura'"
            @click="chartAxis = 'infraestructura'"
          >
            Infraestructura
          </button>
        </div>
      </div>

      <div class="csv-poblacion-container">
        <BotonDesacargaCSV
          v-show="chartAxis === 'poblacion'"
          :filter-geometries="chartData.filterGeometries || []"
          :population-layers="chartData.populationLayers || []"
          :population-method="chartData.populationMethod || 'polygon'"
        />
        <button
          v-show="!(chartAxis === 'infraestructura' && infraView === 'table')"
          type="button"
          class="chart-type-toggle-btn"
          @click="toggleChartType"
          :title="chartType === 'pie' ? 'Ver como gráfico de barras' : 'Ver como gráfico de torta'"
          :aria-label="chartType === 'pie' ? 'Ver como gráfico de barras' : 'Ver como gráfico de torta'"
        >
          <font-awesome-icon :icon="chartType === 'pie' ? 'chart-bar' : 'chart-pie'" />
          {{ chartType === 'pie' ? 'Ver en barras' : 'Ver en torta' }}
        </button>
        <button
          v-if="chartAxis === 'infraestructura'"
          type="button"
          class="chart-type-toggle-btn"
          @click="toggleInfraView"
          :title="infraView === 'table' ? 'Ver gráficos' : 'Ver tabla'"
          :aria-label="infraView === 'table' ? 'Ver gráficos' : 'Ver tabla'"
        >
          <font-awesome-icon :icon="infraView === 'table' ? 'chart-bar' : 'table'" />
          {{ infraView === 'table' ? 'Ver gráficos' : 'Ver tabla' }}
        </button>
      </div>

      <div v-show="chartAxis === 'infraestructura' && infraView === 'table'" class="layer-select-container">
        <select
          v-model="layerSelected"
          @change="onLayerChange"
          class="form-control"
          aria-label="Seleccionar capa temática"
        >
          <option value="">[Seleccione Capa Temática]</option>
          <optgroup v-for="(category, index) in filteredCategories" :key="index" :label="category.name">
            <option v-for="layer in category.layers" :key="layer.id" :value="layer.id">
              {{ layer.name }}
            </option>
          </optgroup>
        </select>
        <div v-if="isLoadingLayers" class="layers-status is-loading">
           <span class="layers-spinner" aria-hidden="true"></span>
           <span>Cargando capas…</span>
        </div>
        <div v-else-if="!filteredCategories.length" class="layers-status">
           Sin capas disponibles
        </div>
      </div>

      <transition name="fade">
        <div
          v-show="!isHidden && chartAxis === 'poblacion' && hasPopulationData"
          class="chart-axis-section"
        >
          <div v-if="populationTotalCount !== null" class="kpi-row">
            <div class="kpi-card">
              <span class="kpi-value">{{ populationTotalCount.toLocaleString('es-CL') }}</span>
              <span class="kpi-label">Personas en el área analizada</span>
            </div>
          </div>
          <div class="population-section">
            <div v-for="(dataset, index) in populationDatasets" :key="index" class="population-chart-card">
                <div class="population-chart-header">
                    <h5>{{ dataset.title }}</h5>
                    <div class="chart-download-menu">
                      <button
                        type="button"
                        class="chart-download-btn"
                        title="Descargar gráfico como imagen"
                        aria-label="Descargar gráfico como imagen"
                        @click.stop="toggleImageMenu('population', index)"
                      >
                        <font-awesome-icon icon="download" />
                      </button>
                      <div v-if="openImageMenu === 'population-' + index" class="image-menu-dropdown shadow-sm">
                        <button type="button" @click="downloadChartImage('population', index, 'png')">
                          Descargar PNG (sin fondo)
                        </button>
                        <button type="button" @click="downloadChartImage('population', index, 'jpg')">
                          Descargar JPG (con fondo blanco)
                        </button>
                      </div>
                    </div>
                </div>
                <div ref="chartBodyPopulation" class="population-chart-canvas"></div>
            </div>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <!-- Highcharts mide este contenedor al dibujar, así que si se renderiza
             mientras está en display:none sale vacío. En vez de mantenerlo
             visible de más, los watchers de chartAxis/infraView vuelven a
             dibujar cuando la sección se muestra. -->
        <div
          v-show="!isHidden && chartAxis === 'infraestructura' && infraView === 'chart'"
          class="chart-axis-section"
        >
          <div v-if="plumeKpis.length" class="kpi-row">
            <div v-for="(kpi, index) in plumeKpis" :key="index" class="kpi-card">
              <span class="kpi-value">{{ kpi.value }}</span>
              <span class="kpi-label">{{ kpi.label }}</span>
            </div>
          </div>
          <div v-if="infrastructureKpis.length" class="kpi-row">
            <div v-for="(kpi, index) in infrastructureKpis" :key="index" class="kpi-card">
              <span class="kpi-value">{{ kpi.value.toLocaleString('es-CL') }}</span>
              <span class="kpi-label">{{ kpi.label }}</span>
            </div>
          </div>
          <div v-if="!infrastructureCharts.length" class="layers-status">
            No se encontraron elementos de infraestructura en el área analizada.
          </div>
          <div v-else class="population-section">
            <div v-for="(chartDef, index) in infrastructureCharts" :key="chartDef.key" class="population-chart-card">
                <div class="population-chart-header">
                    <h5>{{ chartDef.title }}</h5>
                    <div class="chart-download-menu">
                      <button
                        type="button"
                        class="chart-download-btn"
                        title="Descargar gráfico como imagen"
                        aria-label="Descargar gráfico como imagen"
                        @click.stop="toggleImageMenu('infra', index)"
                      >
                        <font-awesome-icon icon="download" />
                      </button>
                      <div v-if="openImageMenu === 'infra-' + index" class="image-menu-dropdown shadow-sm">
                        <button type="button" @click="downloadChartImage('infra', index, 'png')">
                          Descargar PNG (sin fondo)
                        </button>
                        <button type="button" @click="downloadChartImage('infra', index, 'jpg')">
                          Descargar JPG (con fondo blanco)
                        </button>
                      </div>
                    </div>
                </div>
                <div ref="chartBodyInfra" class="population-chart-canvas"></div>
            </div>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <GeoprocessingTable
          v-show="!isHidden && chartAxis === 'infraestructura' && infraView === 'table'"
          :layerSelected="layerSelected"
          :resultsTable="chartData.results_table"
          :rawResults="chartData.rawResults"
          :extraKpis="plumeKpis"
          :layerSelectedName="selectedLayerName"
          :graphType="graphType"
        />
      </transition>
    </div>
    <div v-show="!isHidden && reportPayload" class="report-download-footer">
      <button
        type="button"
        class="report-download-btn"
        :disabled="isDownloadingReport"
        @click="downloadHtmlReport"
        title="Descargar reporte HTML"
        aria-label="Descargar reporte HTML"
      >
        <font-awesome-icon v-if="isDownloadingReport" icon="spinner" spin />
        <font-awesome-icon v-else icon="download" />
        {{ isDownloadingReport ? 'Generando informe…' : 'Descargar Informe' }}
      </button>
    </div>
  </div>
</template>

<script>
import { renderGeoprocessingChart } from "./graph.js";
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { LayerService } from "@/services/layers/layerService";
import { mapService } from "@/services/map/mapService";
import GeoprocessingTable from "./GeoprocessingTable.vue";
import {
  isPopulationLayer,
  summarizeInfrastructureByLayer,
  summarizeByRadiusExclusive,
  groupVulnerableLayers,
} from "./utils/vulnerableLayers.js";
import BotonDesacargaCSV from "./csv-poblacion/BotonDesacargaCSV.vue";
import { getProp, fetchPopulationFeatures } from "./csv-poblacion/populationFeatures.js";
import { GEOPROCESS_LAYER_ZINDEX, GEOPROCESS_SELECTED_LAYER_ZINDEX } from "./utils/geoprocessZIndex.js";

// El padre (DataContainer) recrea este componente con :key en cada resultado,
// lo que devuelve el panel a su posición inicial. Se guarda la posición
// arrastrada a nivel de módulo para restaurarla entre remontajes; F5 la resetea.
let savedPanelPosition = null;

export default {
  name: "GeoprocessingChart",
  mixins: [draggableDiv],
  components: {
    GeoprocessingTable,
    BotonDesacargaCSV
  },
  props: {
    chartData: { type: Object, required: true },
    title: { type: String, default: "" },
    reportPayload: { type: Object, default: null },
  },
  data() {
    return {
      populationCharts: [],
      infrastructureChartInstances: [],
      chartType: "pie",
      infraView: "chart",
      chartAxis: "poblacion",
      isHidden: false,
      layerSelected: "",
      filteredCategories: [],
      availableLayers: [], // Keeping for internal ref if needed, but UI uses filteredCategories
      isLoadingLayers: false,
      isDownloadingReport: false,
      populationByRadius: [],
      isLoadingPopulationByRadius: false,
      openImageMenu: null
    };
  },
  computed: {
    graphType() {
      return (this.chartData && this.chartData.graphType) || "";
    },
    // Título del panel: nombre del geoproceso, no la key cruda del backend
    // ("Total Buffer"/"Total Infra") que mostraba antes (QA-61).
    resolvedTitle() {
      if (this.title) return this.title;
      const names = {
        InfluenciaRadial: "Influencia Radial",
        InfluenciaPoligonal: "Influencia Poligonal",
        InfluenciaLineal: "Influencia Lineal",
        PlumaDispersion: "Pluma de Dispersión",
        Poblacion: "Población",
      };
      const name = names[this.graphType];
      return name ? "Resultados: " + name : "Resultado Geoproceso";
    },
    // El backend manda population_graphs = {} cuando no hay capas de población;
    // {} es truthy, así que se mide por los datasets ya armados para no dejar
    // la sección "Población" con el encabezado y nada debajo (QA-112).
    hasPopulationData() {
        return this.populationDatasets.length > 0;
    },
    populationDatasets() {
        const popParams = (this.chartData && this.chartData.population) || null;
        const datasets = [];

        // Análogo a "Área de influencia" en Infraestructura: solo con más de
        // un radio (QA-158). Va primero: es el dato más relevante cuando el
        // geoproceso tiene varios radios, no debería quedar fuera de vista.
        if (this.populationByRadius.length > 1) {
            datasets.push({
                title: "Total Población por Área de Influencia",
                data: {
                    graphType: "InfluenciaRadial",
                    "Área de influencia": this.populationByRadius,
                },
            });
        }

        if (popParams) {
            // Exclude graphType and ensure we have arrays
            const keys = Object.keys(popParams).filter(k => k !== "graphType");
            // Región y comuna solo se muestran si el geoproceso abarcó más de una:
            // con un único valor la torta es siempre 100% y no aporta nada (QA-113).
            // Sexo y edad no se filtran, siempre reparten entre varias categorías.
            const geographicKeys = ["Por Región", "Por Comuna"];
            const filteredKeys = keys.filter(k => !geographicKeys.includes(k) ||
                             (Array.isArray(popParams[k]) && popParams[k].length > 1));
            // "Por Comuna" va al final: es el desglose más granular, menos
            // útil como primera lectura que región/sexo/edad.
            const orderedKeys = [
                ...filteredKeys.filter(k => k !== "Por Comuna"),
                ...filteredKeys.filter(k => k === "Por Comuna"),
            ];
            datasets.push(...orderedKeys.map(k => ({
                title: k,
                data: {
                    graphType: "Poblacion",
                    [k]: popParams[k]
                }
            })));
        }

        return datasets;
    },
    populationTotalCount() {
        if (!this.hasPopulationData) return null;
        const porSexo = (this.chartData.population && this.chartData.population["Por Sexo"]) || [];
        return porSexo.reduce((sum, d) => sum + (d.features_count || 0), 0);
    },
    infrastructureCharts() {
        const rawResults = (this.chartData && this.chartData.rawResults) || [];
        if (!rawResults.length) return [];

        const charts = [];

        const seenByRegion = new Map();  // region  -> Set(layerId::featureKey)

        for (const item of rawResults) {
            const layer = item.layer || {};
            // Compatibilidad con resultados previos que todavía incluían la
            // capa censal dentro de `results`: nunca debe sumar infraestructura.
            if (isPopulationLayer(layer)) continue;
            const layerId = String(layer.id || "");
            const features = (item.layer_results && (item.layer_results.features || item.layer_results.data)) || [];

            for (const feature of features) {
                const props = feature.properties || feature.attributes || {};
                const fKey = this.featureKey(feature);
                const globalKey = layerId + "::" + fKey;

                const region = props.region || props.REGION;
                if (region) {
                    if (!seenByRegion.has(region)) seenByRegion.set(region, new Set());
                    seenByRegion.get(region).add(globalKey);
                }
            }
        }

        // KPI y torta general: mismas filas que usa el reporte (radio exterior
        // en procesos acumulativos), sin deduplicar por OBJECTID.
        const porCapa = summarizeInfrastructureByLayer(rawResults, this.graphType);
        const totalCapa = porCapa.reduce((sum, row) => sum + row.features_count, 0) || 1;

        if (porCapa.length > 0) {
            charts.push({
                key: "total-infra",
                title: "Total Infraestructuras",
                data: {
                    graphType: "InfluenciaPoligonal",
                    "Total Infraestructuras": porCapa.map(row => ({
                        ...row,
                        percentage: Math.round((row.features_count / totalCapa) * 10000) / 100,
                    })),
                },
            });
        }

        // El backend solo arma este desglose por radio para InfluenciaRadial;
        // para InfluenciaLineal (que también acepta más de un radio) colapsa
        // al radio exterior y nunca lo expone, así que se calcula acá con
        // rawResults para que aplique a ambos (QA-158 ampliado).
        const porBuffer = summarizeByRadiusExclusive(rawResults, this.graphType);
        if (porBuffer.length > 1) {
            charts.push({
                key: "total-buffer",
                title: "Total Infraestructuras por Área de Influencia",
                data: { graphType: "InfluenciaRadial", "Total Buffer": porBuffer },
            });
        }

        if (seenByRegion.size > 1) {
            const porRegion = Array.from(seenByRegion.entries());
            const totalRegion = porRegion.reduce((sum, [, set]) => sum + set.size, 0) || 1;
            charts.push({
                key: "por-region",
                title: "Infraestructuras por Región",
                data: {
                    graphType: "InfraestructuraPorRegion",
                    "Infraestructuras por Región": porRegion.map(([region, set]) => ({
                        name: region,
                        features_count: set.size,
                        percentage: Math.round((set.size / totalRegion) * 10000) / 100,
                    })),
                },
            });
        }

        return charts;
    },
    // chartData.filterGeometry es siempre el PRIMER anillo (el radio más
    // chico): filtrar el WMS con eso deja fuera del mapa los elementos que
    // caen entre el radio 1 y el exterior, aunque sí aparecieran en la tabla
    // (que consulta el backend directo, sin este filtro).
    outerFilterGeometry() {
      const geometries = (this.chartData && this.chartData.filterGeometries) || [];
      // Las bandas horarias de la pluma de dispersión no son concéntricas
      // (cada una cubre solo su tramo de avance, ver PlumeGeometry.generate):
      // filtrar solo por la de mayor radio deja fuera las bandas más
      // cercanas al origen. Se combinan todas en un MultiPolygon.
      if (geometries.length > 1 && this.graphType === "PlumaDispersion") {
        return { type: "MultiPolygon", coordinates: geometries.map(g => g.coordinates) };
      }
      if (geometries.length > 0) {
        return geometries.reduce((outer, current) => (
          (current.radius || 0) > (outer.radius || 0) ? current : outer
        ), geometries[0]);
      }
      return (this.chartData && this.chartData.filterGeometry) || null;
    },
    infrastructureKpis() {
        const totalInfra = this.infrastructureCharts.find(c => c.key === "total-infra");
        if (!totalInfra) return [];
        const items = totalInfra.data["Total Infraestructuras"];
        const kpis = [
            {
                label: "Elementos de infraestructura afectados",
                value: items.reduce((sum, d) => sum + d.features_count, 0),
            },
            { label: "Capas con elementos encontrados", value: items.length },
        ];
        const porRegion = this.infrastructureCharts.find(c => c.key === "por-region");
        if (porRegion) {
            kpis.push({
                label: "Regiones involucradas",
                value: porRegion.data["Infraestructuras por Región"].length,
            });
        }
        return kpis;
    },
    selectedLayerName() {
        const layer = this.filteredCategories.flatMap(cat => cat.layers).find(l => String(l.id) === String(this.layerSelected));
        return layer ? layer.name : "";
    },
    plumeKpis() {
        const params = this.chartData && this.chartData.plumeParams;
        if (!params) return [];
        return [
            { label: "Horas de desarrollo", value: `${params.tiempo} h` },
            { label: "Velocidad del viento", value: `${params.velocidad} km/h` },
            { label: "Dirección del viento", value: `${params.direccion}°` },
        ];
    },
  },
  mounted() {
    this.makeDraggable("geoprocessingChartID");
    // Usar $el y no getElementById: durante el remontaje por :key conviven un
    // instante el panel nuevo y el viejo con el mismo id en el DOM.
    if (savedPanelPosition && this.$el) {
      this.$el.style.top = savedPanelPosition.top;
      this.$el.style.left = savedPanelPosition.left;
    }
    // this.extractLayers(); // Deprecated in favor of store fetching
    if (!this.hasPopulationData) {
      this.chartAxis = "infraestructura";
    }
    // El geoproceso reclamó la interacción del mapa para dibujar el área de
    // selección (Geoprocessings.vue); ya con el resultado en pantalla no
    // sigue dibujando nada, así que suelta el clic para que la herramienta
    // "Consulta" pueda volver a responder y mostrar el popup.
    this.$store.commit("releaseMapInteraction", "geoprocessing");
    this.populateLayers().then(() => {
      this.publishGeoprocessLayerRequests();
      this.addAllResultLayers();
    });
    this.loadPopulationByRadius();
    this.renderChart();
    document.addEventListener("click", this.closeImageMenu);
  },
  methods: {
    async downloadHtmlReport() {
        if (!this.reportPayload || this.isDownloadingReport) return;
        this.isDownloadingReport = true;
        try {
            const url = process.env.VUE_APP_API_GEOPROCESS + this.reportPayload.type + "/report";
            // Preferimos la foto del catálogo utilizada durante el geoproceso:
            // así el reporte siempre representa exactamente las capas procesadas.
            // La consulta queda como compatibilidad con resultados anteriores.
            let catalogo = this.reportPayload.vulnerable_catalog;
            if (!Array.isArray(catalogo)) {
                const vulnerables = await LayerService.getVulnerableLayers();
                catalogo = (vulnerables || []).map(l => ({
                    id: l.id,
                    id_category: l.id_category,
                    category_name: l.category_name,
                    cap_poblacion: l.cap_poblacion,
                    report_icon_svg: l.report_icon_svg,
                }));
            }
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ catalogo, result: this.reportPayload }),
            });
            const contentType = response.headers.get("content-type") || "";
            if (!response.ok || !contentType.includes("text/html")) {
                throw new Error(`Respuesta inesperada del servidor (status ${response.status}, tipo "${contentType}")`);
            }
            const disposition = response.headers.get("content-disposition") || "";
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `informe_${Date.now()}.html`;
            const html = await response.text();
            const blob = new Blob([html], { type: contentType });

            // Create link and trigger download
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error al descargar el reporte HTML:", error);
            this.$store.dispatch("toast/show", { message: "Error al descargar el reporte", type: "error" });
        } finally {
            this.isDownloadingReport = false;
        }
    },
    // Elegir una capa en el select oculta el resto de capas del resultado
    // (todas ya están agregadas desde el montaje del panel, ver
    // addAllResultLayers) y resalta la elegida por delante.
    onLayerChange() {
      this.applyResultLayersZIndex();
    },
    // Publica en el store qué capas necesita este resultado y con qué
    // recorte, para que Lista de Capas pueda mantenerlas visibles dentro
    // del área de influencia si el usuario las apaga manualmente.
    publishGeoprocessLayerRequests() {
      const filterGeometry = this.outerFilterGeometry;
      const requests = {};
      this.filteredCategories.flatMap(cat => cat.layers).forEach((layer) => {
        requests[layer.id] = { layer, filterGeometry };
      });
      this.$store.dispatch("setGeoprocessLayerRequests", requests);
    },
    // Agrega al mapa, recortadas al área de influencia, todas las capas
    // vulnerables del resultado que aún no estén activas (si el usuario ya
    // la tenía activa en Lista de Capas, no se toca ni se recorta).
    async addAllResultLayers() {
      const map = mapService.get();
      if (!map) return;
      const filterGeometry = this.outerFilterGeometry;
      const layers = this.filteredCategories.flatMap(cat => cat.layers);
      await Promise.all(layers.map(async (layer) => {
        if (mapService.layerExists(layer.id)) return;
        await LayerService.addLayerToMap(layer, filterGeometry, false);
        // Para que la herramienta "Consulta" pueda mostrar su popup al hacer
        // click, igual que ya hace con las capas activas de Lista de Capas.
        this.$store.dispatch("addGeoprocessResultLayer", layer);
      }));
      this.applyResultLayersZIndex();
    },
    // Todas las capas del resultado por delante del área de influencia
    // (zIndex 9999) y la pluma (zIndex 1000); la elegida en el select, por
    // delante de las demás.
    applyResultLayersZIndex() {
      const map = mapService.get();
      if (!map) return;
      const layerIds = this.filteredCategories.flatMap(cat => cat.layers.map(l => String(l.id)));
      map.getLayers().forEach((mapLayer) => {
        if (!mapLayer) return;
        const id = String(mapLayer.get("id"));
        if (!layerIds.includes(id)) return;
        const isSelected = id === String(this.layerSelected);
        mapLayer.setVisible(!this.layerSelected || isSelected);
        mapLayer.setZIndex(isSelected ? GEOPROCESS_SELECTED_LAYER_ZINDEX : GEOPROCESS_LAYER_ZINDEX);
      });
    },
    // Quita del mapa las capas del resultado que el usuario no tenga activas
    // en Lista de Capas ahora mismo (las haya agregado este panel, o hayan
    // quedado recortadas tras un apagado/prendido mientras el geoproceso
    // seguía activo, ver Layers.vue).
    cleanupResultLayers() {
      const activeIds = new Set((this.$store.getters.getActiveLayers || []).map(l => String(l.id)));
      const resultLayerIds = this.filteredCategories.flatMap(cat => cat.layers.map(l => String(l.id)));
      resultLayerIds.forEach((id) => {
        this.$store.dispatch("removeGeoprocessResultLayer", { id });
        if (!activeIds.has(id)) {
          mapService.removeLayerFromMapByID(id);
        }
      });
      this.$store.dispatch("setGeoprocessLayerRequests", {});
    },
    async populateLayers() {
       this.isLoadingLayers = true;

       try {
         const resultsTable = (this.chartData && this.chartData.results_table) || {};
         const idsConDatos = new Set(
            Object.keys(resultsTable).filter(id => (resultsTable[id] || []).length > 0)
         );
         const availableLayers = (this.chartData && this.chartData.availableLayers) || [];
         const layersConDatos = availableLayers.filter(
            layer => idsConDatos.has(String(layer.id))
         );
         this.filteredCategories = groupVulnerableLayers(layersConDatos);

       } catch (error) {
          console.error("Error populating layers:", error);
       } finally {
          this.isLoadingLayers = false;
       }
    },
    extractLayers() {
       // Deprecated method, kept empty or remove if clean cleanup desired
    },
    // QA-158: la respuesta del geoproceso solo trae agregados de población
    // con el radio exterior (no hay desglose por anillo), así que se
    // consulta la capa censal directo por cada anillo, igual mecanismo que
    // usa el botón de CSV. Cuenta personas (tot_pob), no manzanas.
    async loadPopulationByRadius() {
      const geometries = (this.chartData && this.chartData.filterGeometries) || [];
      const layers = (this.chartData && this.chartData.populationLayers) || [];
      const method = (this.chartData && this.chartData.populationMethod) || "polygon";
      // Las bandas horarias de la pluma no son concéntricas (ver
      // PlumeGeometry.generate): cada una cubre solo su tramo, así que el
      // total por banda ya es exclusivo. Los buffers radial/lineal sí son
      // acumulativos y se identifican por "radius".
      const isPlume = this.graphType === "PlumaDispersion";
      const key = isPlume ? "hour" : "radius";

      const sorted = geometries
        .filter(g => g[key] != null)
        .slice()
        .sort((a, b) => a[key] - b[key]);

      if (sorted.length <= 1 || !layers.length) {
        this.populationByRadius = [];
        return;
      }

      this.isLoadingPopulationByRadius = true;
      try {
        const totals = [];
        for (const geometry of sorted) {
          let total = 0;
          for (const layer of layers) {
            const features = await fetchPopulationFeatures(layer, geometry, method);
            for (const feature of features) {
              const props = feature.properties || feature.attributes || {};
              total += Number(getProp(props, "tot_pob")) || 0;
            }
          }
          totals.push({ [key]: geometry[key], total });
        }

        let counts;
        if (isPlume) {
          counts = totals.map(t => ({ hour: t.hour, features_count: t.total }));
        } else {
          // Los buffers son acumulativos (el mayor contiene al menor): se
          // resta el anillo anterior para obtener el tramo exclusivo, mismo
          // criterio que usa el backend para infraestructura.
          let previous = 0;
          counts = totals.map(t => {
            const features_count = t.total - previous;
            previous = t.total;
            return { radius: t.radius, features_count };
          });
        }
        const total = counts.reduce((sum, t) => sum + t.features_count, 0) || 1;
        this.populationByRadius = counts.map(t => ({
          ...t,
          percentage: Math.round((t.features_count / total) * 10000) / 100,
        }));
      } catch (error) {
        console.error("Error calculando población por radio:", error);
        this.populationByRadius = [];
      } finally {
        this.isLoadingPopulationByRadius = false;
        this.$nextTick(() => this.renderChart());
      }
    },
    featureKey(feature) {
        const props = feature.properties || feature.attributes || {};
        if (props.objectid !== undefined) return String(props.objectid);
        if (props.OBJECTID !== undefined) return String(props.OBJECTID);
        return JSON.stringify(props);
    },
    toggleVisible() {
      this.isHidden = !this.isHidden;
    },
    toggleChartType() {
      this.chartType = this.chartType === "pie" ? "column" : "pie";
      this.renderChart();
    },
    toggleInfraView() {
      this.infraView = this.infraView === "table" ? "chart" : "table";
    },
    closeChart() {
      this.cleanupResultLayers();
      this.$emit("close-chart");
    },
    renderChart() {
      if (this.populationCharts.length) {
          this.populationCharts.forEach(c => c.destroy());
          this.populationCharts = [];
      }
      if (this.hasPopulationData) {
          this.$nextTick(() => {
              const refs = this.$refs.chartBodyPopulation;
              if (Array.isArray(refs)) {
                  refs.forEach((el, index) => {
                      const dataset = this.populationDatasets[index];
                      if (dataset) {
                          // Los gráficos de población (Por Región/Comuna/
                          // Sexo/Edad/Área de influencia) cuentan personas (QA-61)
                          const countLabel = (dataset.title.startsWith("Por ") || dataset.title === "Total Población por Área de Influencia") ? "Personas" : "Cantidad";
                          const chartInstance = renderGeoprocessingChart(el, dataset.data, this.chartType, countLabel);
                          if (chartInstance) this.populationCharts.push(chartInstance);
                      }
                  });
              }
          });
      }

      if (this.infrastructureChartInstances.length) {
          this.infrastructureChartInstances.forEach(c => c.destroy());
          this.infrastructureChartInstances = [];
      }
      if (this.infrastructureCharts.length) {
          this.$nextTick(() => {
              const refs = this.$refs.chartBodyInfra;
              if (Array.isArray(refs)) {
                  refs.forEach((el, index) => {
                      const chartDef = this.infrastructureCharts[index];
                      if (chartDef) {
                          const chartInstance = renderGeoprocessingChart(el, chartDef.data, this.chartType, "Elementos");
                          if (chartInstance) this.infrastructureChartInstances.push(chartInstance);
                      }
                  });
              }
          });
      }
    },
    toggleImageMenu(kind, index) {
      const key = `${kind}-${index}`;
      this.openImageMenu = this.openImageMenu === key ? null : key;
    },
    closeImageMenu() {
      this.openImageMenu = null;
    },
    downloadChartImage(kind, index, format) {
      const instance = kind === "population"
        ? this.populationCharts[index]
        : this.infrastructureChartInstances[index];
      this.openImageMenu = null;
      if (!instance) return;

      const dataset = kind === "population"
        ? this.populationDatasets[index]
        : this.infrastructureCharts[index];
      const section = kind === "population" ? "Poblacion" : "Infraestructura";
      const title = (dataset && dataset.title) || "Grafico";
      // Sin tildes ni espacios: "Por Comuna" -> "PorComuna", coherente con
      // que el título original ya capitaliza cada palabra.
      const sanitizedTitle = title
        .normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "");
      const filename = `${section}_${sanitizedTitle}`;

      // JPG no soporta transparencia: sin forzar fondo blanco, el canal
      // alfa del chart (transparent) queda compuesto sobre negro en la
      // mayoría de los visores. PNG hereda el fondo transparente tal cual
      // está configurado en graph.js.
      const isJpg = format === "jpg";
      const exportingOptions = { type: isJpg ? "image/jpeg" : "image/png", filename };
      const chartOptions = isJpg ? { chart: { backgroundColor: "#ffffff" } } : {};

      // exportChart() siempre manda la petición al servidor remoto de
      // Highcharts (export.highcharts.com), sin importar si offline-exporting
      // está cargado. exportChartLocal() es el método que ese módulo agrega
      // específicamente para exportar 100% client-side, sin red.
      instance.exportChartLocal(exportingOptions, chartOptions);
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeImageMenu);
    // Leer la posición del elemento PROPIO (this.$el): con getElementById se
    // capturaba el panel nuevo recién insertado y se guardaba su posición inicial.
    if (this.$el && this.$el.style.top && this.$el.style.left) {
      savedPanelPosition = { top: this.$el.style.top, left: this.$el.style.left };
    }
    this.cleanupResultLayers();
    if (this.populationCharts.length) {
        this.populationCharts.forEach(c => c.destroy());
    }
    if (this.infrastructureChartInstances.length) {
        this.infrastructureChartInstances.forEach(c => c.destroy());
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
    // Si los resultados llegan con la tabla abierta, el gráfico se dibuja
    // contra un contenedor oculto y queda de tamaño cero. Al volver a la
    // pestaña del gráfico hay que redibujarlo, ya con el contenedor visible.
    chartAxis() {
      this.$nextTick(() => this.renderChart());
    },
    infraView(view) {
      if (view === "chart") {
        this.$nextTick(() => this.renderChart());
      }
    },
    hasPopulationData(val) {
      if (!val && this.chartAxis === "poblacion") {
        this.chartAxis = "infraestructura";
      }
    },
  },
};
</script>


<style scoped>
/* La ventana conserva su posición y tamaño propios, pero comparte el lenguaje
   visual de las demás herramientas del mapa mediante .map-tool-window. */
.chart-wrapper.map-tool-window {
  position: absolute;
  top: 100px;
  left: 350px;
  width: 570px;
  height: 70vh;
  z-index: 5000;
  display: flex;
  flex-direction: column;
  resize: both;
  overflow: hidden;
  min-width: 320px;
  min-height: 240px;
  max-width: 95vw;
  max-height: 90vh;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(14, 42, 64, 0.2);
  color: var(--color-graphite, #2b3138);
  font-family: "Public Sans", system-ui, sans-serif;
}

.chart-wrapper.map-tool-window.is-collapsed {
  height: auto !important;
  min-height: 0;
  resize: none;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;
  min-height: 44px;
  padding: 8px 10px 8px 15px;
  background: var(--color-navy, #0e2a40);
  border: 0;
  color: var(--color-white, #fff);
}

.results-header h5 {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--color-mist, #f2f4f6);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.35px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 10px;
}

.results-header-actions {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 2px;
}

.results-header-actions .results-control-btn {
  width: 32px;
  height: 28px;
  min-width: 32px;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Pestañas Población/Infraestructura: mismo componente visual .btn-tab,
   sólo que con etiqueta de texto en vez de ícono. */
.btn-tab--text {
  min-width: auto;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
}

.csv-poblacion-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
}

.chart-type-toggle-btn {
  flex-shrink: 0;

  display: inline-flex;
  align-items: center;
  gap: 7px;

  min-height: 32px;
  padding: 6px 14px;

  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 4px;

  color: var(--color-navy, #0e2a40);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;

  cursor: pointer;

  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.chart-type-toggle-btn:hover {
  background: var(--color-fog, #dbe1e6);
  border-color: var(--color-steel, #3b6e94);
}

/* Fila de KPIs arriba de cada set de gráficos: el conteo se lee sin pasar
   el cursor por encima de ninguna porción. */
.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  padding: 14px 16px 18px;
}

.kpi-card {
  flex: 1 1 120px;

  display: flex;
  flex-direction: column;
  gap: 2px;

  padding: 7px 10px;

  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-left: 3px solid var(--color-orange, #e8730c);
  border-radius: 6px;
}

.kpi-value {
  color: var(--color-navy, #0e2a40);
  font-size: 1.05rem;
  font-weight: 700;
}

.kpi-label {
  color: var(--color-ghost, #8a929a);
  font-size: 0.68rem;
  line-height: 1.3;
}

.results-header-actions .results-control-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.16);
  color: var(--color-white, #fff);
}

.results-header-actions .results-control-btn:focus-visible {
  outline: 2px solid var(--color-orange, #e8730c);
  outline-offset: 1px;
}

.results-header-actions .results-control-btn:disabled {
  cursor: wait;
  opacity: 0.5;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.layer-selector-container {
  padding: 12px 16px;
  background: var(--color-white, #fff);
  border-bottom: 1px solid var(--color-line, #dbe1e6);
}

/* Sin fondo ni borde propios: se funde con el gris de .chart-scroll-container.
   Sin padding-bottom: la separación de la tabla la da el padding-top propio
   de GeoprocessingTable.vue. */
.layer-select-container {
  padding: 10px 16px 0;
}

.report-download-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;

  padding: 12px 16px;

  background: var(--color-white, #fff);
  border-top: 1px solid var(--color-line, #dbe1e6);
}

.report-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  width: 100%;
  min-height: 40px;
  padding: 8px 14px;

  background: var(--color-orange, #e8730c);
  border: 1px solid var(--color-orange, #e8730c);
  border-radius: 4px;

  color: var(--color-white, #fff);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;

  cursor: pointer;

  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.report-download-btn:hover:not(:disabled) {
  background: #c9630a;
  border-color: #c9630a;
}

.report-download-btn:disabled {
  background: var(--color-fog, #dbe1e6);
  border-color: var(--color-line, #dbe1e6);
  color: var(--color-ghost, #8a929a);
  cursor: wait;
}

.form-control {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 4px;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  color: var(--color-graphite, #2b3138);
  font-family: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  transition: border-color 0.2s ease, outline-color 0.2s ease;
}

.form-control:hover {
  border-color: var(--color-steel, #3b6e94);
}

.form-control:focus {
  border-color: var(--color-steel, #3b6e94);
  outline: 2px solid var(--color-fog, #dbe1e6);
  outline-offset: 1px;
}

.chart-scroll-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* El botón de descargar informe queda fijo (absolute) sobre el fondo del
     panel: sin este padding, el scroll hasta el final tapa el último
     gráfico detrás del botón. */
  padding-bottom: 68px;
  background: var(--color-mist, #f2f4f6);
  scrollbar-color: var(--color-steel, #3b6e94) transparent;
  scrollbar-width: thin;
}

.tabs-container {
  display: flex;
  width: fit-content;
  margin: 0 auto;
  padding: 3px;
  gap: 3px;
  justify-content: center;
  background: var(--color-mist, #f2f4f6);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
}

.btn-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  min-height: 32px;
  padding: 6px 18px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  color: var(--color-ghost, #8a929a);
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.btn-tab:hover {
  background: var(--color-white, #fff);
  color: var(--color-navy, #0e2a40);
}

.btn-tab:focus-visible {
  outline: 2px solid var(--color-orange, #e8730c);
  outline-offset: 2px;
}

.btn-tab.active {
  background: var(--color-navy, #0e2a40);
  color: var(--color-white, #fff);
  border-color: var(--color-navy, #0e2a40);
  box-shadow: 0 2px 5px rgba(14, 42, 64, 0.16);
}

.population-section {
  padding: 0 16px 2px;
}

.population-chart-card {
  margin: 0 0 16px;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
}

.population-chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  padding: 9px 12px;
  background: var(--color-navy, #0e2a40);
  border-left: 4px solid var(--color-orange, #e8730c);
  border-radius: 6px 6px 0 0;
}

.population-chart-header h5 {
  margin: 0;
  color: var(--color-white, #fff);
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.25px;
}

.chart-download-btn {
  flex-shrink: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 26px;
  height: 26px;
  padding: 0;

  background: transparent;
  border: 0;
  border-radius: 4px;

  color: rgba(255, 255, 255, 0.82);
  font-size: 0.8rem;

  cursor: pointer;

  transition: background-color 0.2s ease, color 0.2s ease;
}

.chart-download-btn:hover {
  background: rgba(255, 255, 255, 0.16);
  color: var(--color-white, #fff);
}

.chart-download-menu {
  position: relative;
  flex-shrink: 0;
}

.image-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;

  overflow: hidden;
  width: max-content;
  min-width: 210px;

  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
  box-shadow: 0 8px 22px rgba(14, 42, 64, 0.16);

  z-index: 1000;
}

.image-menu-dropdown button {
  display: block;
  width: 100%;
  padding: 9px 12px;

  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--color-line, #dbe1e6);

  color: var(--color-graphite, #2b3138);
  font-family: inherit;
  font-size: 0.8rem;
  text-align: left;

  cursor: pointer;
  transition: background-color 0.2s ease;
}

.image-menu-dropdown button:last-child {
  border-bottom: 0;
}

.image-menu-dropdown button:hover {
  background: var(--color-fog, #dbe1e6);
}

.population-chart-canvas {
  min-height: 380px;
  padding: 8px 10px 10px;
}

/* Estado del selector de capas: cargando y sin resultados */

.layers-status {
  display: flex;
  align-items: center;
  gap: 7px;

  margin-top: 5px;
  padding: 5px 9px;

  background: var(--color-mist, #f2f4f6);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 4px;

  color: var(--color-ghost, #8a929a);
  font-size: 0.78rem;
  line-height: 1.3;
}

.layers-status.is-loading {
  color: var(--color-navy, #0e2a40);
}

.layers-spinner {
  flex-shrink: 0;

  width: 12px;
  height: 12px;

  border: 2px solid rgba(14, 42, 64, 0.18);
  border-top-color: var(--color-orange, #e8730c);
  border-radius: 50%;

  animation: layers-spin 0.7s linear infinite;
}

@keyframes layers-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Respeta a quien pidió menos animación en su sistema */
@media (prefers-reduced-motion: reduce) {
  .layers-spinner {
    animation-duration: 2.4s;
  }
}

@media (max-width: 900px) {
  .chart-wrapper.map-tool-window {
    top: 80px;
    left: 16px;
    width: calc(100vw - 32px);
    max-width: 570px;
  }
}

@media (max-width: 700px) {
  .chart-wrapper.map-tool-window {
    top: 72px;
    left: 8px;
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    height: calc(100vh - 88px);
    max-height: calc(100vh - 88px);
    resize: none;
  }

  .results-header {
    padding-left: 12px;
  }

  .kpi-row {
    padding-right: 10px;
    padding-left: 10px;
  }

  .layer-selector-container {
    padding-right: 10px;
    padding-left: 10px;
  }

  .population-section {
    padding-right: 10px;
    padding-left: 10px;
  }
}
</style>
